import { useSearchParams } from 'next/navigation';

import { Button } from '@paid-ui/components/button';
import { Select, SelectItem } from '@paid-ui/components/select';
import { ChevronLeftIcon } from '@paid-ui/icons/chevron-left';
import { ChevronRightIcon } from '@paid-ui/icons/chevron-right';
import { ChevronsLeftIcon } from '@paid-ui/icons/chevrons-left';
import { ChevronsRightIcon } from '@paid-ui/icons/chevrons-right';

import { useApplyFilters } from './use-apply-filter';

interface Props {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  defaultPageSize: number;
  disabled?: boolean;
  hidden?: boolean;
}

/** Multiples of the three-column grid, so the last row is never a stub. */
const pageSizes = [12, 24, 48];

export const CardsPagination: React.FC<Props> = ({
  currentPage,
  totalPages,
  pageSize,
  defaultPageSize,
  disabled,
  hidden,
}) => {
  const searchParams = useSearchParams();
  const applyFilters = useApplyFilters();

  if (hidden) {
    return null;
  }

  const lastPage = Math.max(totalPages - 1, 0);
  const canPrevious = currentPage > 0;
  const canNext = currentPage < lastPage;

  const goToPage = (page: number) => {
    // The page number is not a filter — changing it must not reset itself.
    applyFilters({ page: page > 0 ? String(page) : '' }, { resetPage: false });
  };

  const handlePageSizeChange = (value: string) => {
    if (value === searchParams.get('size')) return;
    applyFilters({ size: Number(value) === defaultPageSize ? '' : value });
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 border-t border-grey-mid pt-4">
      <div className="flex items-center gap-x-2">
        <p className="shrink-0 text-sm font-medium">Show</p>
        <Select
          compact
          className="h-8 text-sm"
          sideOffset={4}
          disabled={disabled}
          placeholder={String(pageSize)}
          value={String(pageSize)}
          onValueChange={handlePageSizeChange}
        >
          {pageSizes.map((size) => (
            <SelectItem key={size} value={String(size)} className="text-sm">
              {size}
            </SelectItem>
          ))}
        </Select>
        <p className="shrink-0 text-sm text-grey-dark">per page</p>
      </div>

      <div className="flex items-center gap-x-4">
        <p className="text-sm font-medium">
          Page {currentPage + 1} of {Math.max(totalPages, 1)}
        </p>
        <div className="flex items-center gap-x-2">
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            disabled={disabled || !canPrevious}
            onClick={() => goToPage(0)}
          >
            <span className="sr-only">Go to first page</span>
            <ChevronsLeftIcon />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            disabled={disabled || !canPrevious}
            onClick={() => goToPage(currentPage - 1)}
          >
            <span className="sr-only">Go to previous page</span>
            <ChevronLeftIcon />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            disabled={disabled || !canNext}
            onClick={() => goToPage(currentPage + 1)}
          >
            <span className="sr-only">Go to next page</span>
            <ChevronRightIcon />
          </Button>
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            disabled={disabled || !canNext}
            onClick={() => goToPage(lastPage)}
          >
            <span className="sr-only">Go to last page</span>
            <ChevronsRightIcon />
          </Button>
        </div>
      </div>
    </div>
  );
};
